import axios from 'axios';
import localforage from 'localforage';
import { saveToCache, getFromCache } from './cacheService';

const RAW_API_URL = process.env.REACT_APP_API_URL || 'https://pos.ai961.com/api';
const API_URL = RAW_API_URL.endsWith('/') ? RAW_API_URL.slice(0, -1) : RAW_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const OFFLINE_QUEUE_KEY = 'offline_request_queue';

const shouldQueueRequest = (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return false;
  if (!config.url) return false;
  if (config.url.startsWith('/auth/') || config.url.includes('/auth/')) return false;
  return true;
};

const enqueueRequest = async (config) => {
  const queue = (await localforage.getItem(OFFLINE_QUEUE_KEY)) || [];
  const entry = {
    url: config.url,
    method: config.method,
    data: config.data,
    params: config.params,
    timestamp: Date.now(),
  };
  queue.push(entry);
  await localforage.setItem(OFFLINE_QUEUE_KEY, queue);
};

const syncQueuedRequests = async () => {
  const queue = (await localforage.getItem(OFFLINE_QUEUE_KEY)) || [];
  if (!queue.length) return;

  console.log(`[Offline Queue] Syncing ${queue.length} queued requests...`);
  const remaining = [];
  for (const entry of queue) {
    try {
      let payload = entry.data;
      const isCreateSale = entry.url?.includes('/sales/create_sale/');

      if (isCreateSale && typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (_e) {
          payload = entry.data;
        }
      }

      if (isCreateSale && payload && typeof payload === 'object' && !payload.client_sale_date && entry.timestamp) {
        payload = {
          ...payload,
          client_sale_date: new Date(entry.timestamp).toISOString(),
        };
      }

      await api.request({
        url: entry.url,
        method: entry.method,
        data: payload,
        params: entry.params,
      });
      console.log(`[Offline Queue] ✓ Synced: ${entry.method.toUpperCase()} ${entry.url}`);
    } catch (error) {
      console.error(`[Offline Queue] ✗ Failed: ${entry.method.toUpperCase()} ${entry.url}`, error);
      remaining.push(entry);
    }
  }

  await localforage.setItem(OFFLINE_QUEUE_KEY, remaining);
  if (remaining.length === 0) {
    console.log('[Offline Queue] All requests synced successfully!');
  } else {
    console.warn(`[Offline Queue] ${remaining.length} requests still pending`);
  }
};

export const getOfflineQueueCount = async () => {
  const queue = (await localforage.getItem(OFFLINE_QUEUE_KEY)) || [];
  return queue.length;
};

window.addEventListener('online', () => {
  console.log('[Offline] Connection restored, syncing queued requests...');
  syncQueuedRequests();
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Cannot access localStorage for token:', error);
      // Continue without token - user might be in private browsing
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh
api.interceptors.response.use(
  (response) => {
    // Auto-save GET responses to cache
    const method = (response.config.method || 'get').toLowerCase();
    const url = response.config.url || '';
    
    if (method === 'get' && url.startsWith('/api/')) {
      const path = url;
      
      // Cache specific endpoints
      if (path.includes('/items/') && !path.includes('/items/batches/') && !path.includes('/low_stock/')) {
        console.log('[Cache] Saving ITEMS to cache');
        saveToCache('ITEMS', response.data.results || response.data);
      } else if (path.includes('/items/batches/') && !path.includes('/alert_batches/') && !path.includes('/expired_batches/') && !path.includes('/lookup/')) {
        console.log('[Cache] Saving BATCHES to cache');
        saveToCache('BATCHES', response.data.results || response.data);
      } else if (path.includes('/sales/') && !path.includes('/sales/create_sale') && !path.includes('/sales/profit_report') && !path.includes('/sales/sales_chart') && !path.includes('/sales/receipt/')) {
        console.log('[Cache] Saving RECEIPTS to cache');
        saveToCache('RECEIPTS', response.data.results || response.data);
      } else if (path.includes('/items/categories/')) {
        console.log('[Cache] Saving CATEGORIES to cache');
        saveToCache('CATEGORIES', response.data.results || response.data);
      } else if (path.includes('/items/brands/')) {
        console.log('[Cache] Saving BRANDS to cache');
        saveToCache('BRANDS', response.data.results || response.data);
      }
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Try to use cached data if offline
    if (!error.response && !navigator.onLine) {
      const path = originalRequest.url;
      let cachedData = null;

      if (path.includes('/items/') && !path.includes('/items/batches/')) {
        cachedData = await getFromCache('ITEMS');
      } else if (path.includes('/items/batches/')) {
        cachedData = await getFromCache('BATCHES');
      } else if (path.includes('/sales/') && !path.includes('/sales/create_sale')) {
        cachedData = await getFromCache('RECEIPTS');
      } else if (path.includes('/items/categories/')) {
        cachedData = await getFromCache('CATEGORIES');
      } else if (path.includes('/items/brands/')) {
        cachedData = await getFromCache('BRANDS');
      }

      if (cachedData) {
        console.log(`[Offline Cache] ✓ Using cached data for ${path}`);
        return Promise.resolve({
          data: cachedData,
          config: originalRequest,
          status: 200,
          statusText: 'OK (from cache)',
          headers: {},
        });
      }
    }

    const isOfflineNetworkError = !error.response && !navigator.onLine;
    if (originalRequest && shouldQueueRequest(originalRequest) && isOfflineNetworkError) {
      console.log(`[Offline] Queuing request: ${originalRequest.method.toUpperCase()} ${originalRequest.url}`);
      console.error('[Offline] Error details:', error.message, error.response?.status);
      await enqueueRequest(originalRequest);
      return Promise.resolve({
        data: { queued: true, offline: true },
        config: originalRequest,
      });
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        let refreshToken = null;
        try {
          refreshToken = localStorage.getItem('refresh_token');
        } catch (e) {
          console.warn('Cannot access refresh token');
        }
        
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          try {
            localStorage.setItem('access_token', access);
          } catch (e) {
            console.warn('Cannot save new access token');
          }

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (err) {
        try {
          localStorage.clear();
        } catch (e) {
          console.warn('Cannot clear localStorage');
        }
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password, language) =>
    api.post('/auth/login/', { username, password, language }),
  
  register: (data) =>
    api.post('/auth/register/', data),
  
  logout: (refreshToken) =>
    api.post('/auth/logout/', { refresh: refreshToken }),
  
  getProfile: () =>
    api.get('/auth/profile/'),
  
  updateProfile: (data) =>
    api.put('/auth/profile/', data),
};

export const itemsAPI = {
  getItems: (params) =>
    api.get('/items/', { params }),
  
  getItem: (id) =>
    api.get(`/items/${id}/`),
  
  createItem: (data) =>
    api.post('/items/', data),
  
  updateItem: (id, data) =>
    api.put(`/items/${id}/`, data),
  
  deleteItem: (id) =>
    api.delete(`/items/${id}/`),
  
  getLowStockItems: () =>
    api.get('/items/low_stock/'),
  
  getCategories: () =>
    api.get('/items/categories/'),

  getBrands: () =>
    api.get('/items/brands/'),

  adjustQuantity: (id, adjustment) =>
    api.post(`/items/${id}/adjust_quantity/`, { adjustment }),
  
  setPreferredBatch: (itemId, batchId) =>
    api.post(`/items/${itemId}/set_preferred_batch/`, { batch_id: batchId }),
};

export const batchesAPI = {
  getBatches: (params) =>
    api.get('/items/batches/', { params }),
  
  getBatch: (id) =>
    api.get(`/items/batches/${id}/`),
  
  createBatch: (data) =>
    api.post('/items/batches/', data),
  
  updateBatch: (id, data) =>
    api.patch(`/items/batches/${id}/`, data),
  
  deleteBatch: (id) =>
    api.delete(`/items/batches/${id}/`),
  
  getAlertBatches: () =>
    api.get('/items/batches/alert_batches/'),

  lookupByBarcode: (barcode) =>
    api.get('/items/batches/lookup/', { params: { barcode } }),
  
  getExpiredBatches: () =>
    api.get('/items/batches/expired_batches/'),
  
  generateCode: () =>
    api.get('/items/batches/generate-code/'),
};

export default api;
